import { Dispatch } from "redux";
import { Product } from "../types/productTypes";
import {
  collection,
  query,
  getDocs,
  getDoc,
  where,
  addDoc,
  deleteDoc,
  doc,
  setDoc,
  orderBy,
  limit,
  startAfter,
  getFirestore,
} from "firebase/firestore";
import { db } from "../../firebase";
import { Wishlist } from "../types/wishlistTypes";

export const FETCH_WISHLIST_REQUEST = "FETCH_WISHLIST_REQUEST";
export const FETCH_WISHLIST_SUCCESS = "FETCH_WISHLIST_SUCCESS";
export const FETCH_WISHLIST_FAILURE = "FETCH_WISHLIST_FAILURE";
export const FETCH_WISHLIST_RESET = "FETCH_WISHLIST_RESET";
export const REMOVE_WISHLIST_REQUEST = "REMOVE_WISHLIST_REQUEST";
export const REMOVE_WISHLIST_SUCCESS = "REMOVE_WISHLIST_SUCCESS";
export const REMOVE_WISHLIST_FAILURE = "REMOVE_WISHLIST_FAILURE";

export const PAGE_SIZE = 12;

export const fetchWishlistRequest = () => ({
  type: FETCH_WISHLIST_REQUEST,
});

export const fetchWishlistSuccess = (wishlistPageData: {
  products: Product[];
  wishlists: Wishlist[];
  totalRecords?: number;
}) => ({
  type: FETCH_WISHLIST_SUCCESS,
  payload: wishlistPageData,
});

export const fetchWishlistFailure = (error: string) => ({
  type: FETCH_WISHLIST_FAILURE,
  payload: error,
});

export const removeWishlistRequest = () => ({
  type: REMOVE_WISHLIST_REQUEST,
});

export const removeWishlistSuccess = () => ({
  type: REMOVE_WISHLIST_SUCCESS,
});

export const removeWishlistFailure = (error: string) => ({
  type: REMOVE_WISHLIST_FAILURE,
  payload: error,
});

// Helper function to get user's document ID by createdTime (userId)
const getUserDocumentId = async (userId: number): Promise<string | null> => {
  try {
    const q = query(
      collection(db, "users"),
      where("createdTime", "==", userId)
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    return querySnapshot.docs[0].id;
  } catch (error) {
    console.error("Error getting user document ID:", error);
    return null;
  }
};

export const fetchWishlistProducts =
  (
    userId: number,
    prevLastDocument: string = "",
    recordLimit: number = PAGE_SIZE
  ) =>
  async (dispatch: Dispatch) => {
    dispatch(fetchWishlistRequest());
    try {
      const wishlistPageData: {
        products: Product[];
        wishlists: Wishlist[];
        totalRecords?: number;
      } = {
        products: [],
        wishlists: [],
        totalRecords: 0,
      };

      // Get the user's actual document ID
      const userDocumentId = await getUserDocumentId(userId);
      if (!userDocumentId) {
        throw new Error("User not found");
      }

      // Fetch wishlist items from users/{userDocumentId}/wishlist subcollection ordered by createdTime desc
      const wishlistRef = collection(db, "users", userDocumentId, "wishlist");
      let wishlistQuery = query(wishlistRef, orderBy("createdTime", "desc"));

      // Get total count (without limit)
      const totalCountSnapshot = await getDocs(wishlistQuery);
      const totalRecords = totalCountSnapshot.size;

      // Apply pagination
      if (prevLastDocument) {
        const firestore = getFirestore();
        const docRef = doc(firestore, "users", userDocumentId, "wishlist", prevLastDocument);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          wishlistQuery = query(wishlistQuery, startAfter(docSnap));
        }
      }

      if (recordLimit) {
        wishlistQuery = query(wishlistQuery, limit(recordLimit));
      }

      const wishlistSnapshot = await getDocs(wishlistQuery);

      const wishlists: Wishlist[] = wishlistSnapshot.docs.map((doc) => ({
        documentId: doc.id,
        userId: userId,
        productId: doc.data().productId,
      }));

      // Extract unique product IDs
      const productIds = Array.from(
        new Set(wishlists.map((wishlist) => wishlist.productId))
      );

      // Fetch product details for those IDs
      let wishlistProducts: Product[] = [];
      if (productIds.length > 0) {
        // Firestore 'in' query has a limit of 10 items, so we need to batch if needed
        const batches: number[][] = [];
        for (let i = 0; i < productIds.length; i += 10) {
          batches.push(productIds.slice(i, i + 10));
        }

        const productPromises = batches.map((batch) =>
          getDocs(query(collection(db, "products"), where("createdTime", "in", batch)))
        );

        const productSnapshots = await Promise.all(productPromises);
        wishlistProducts = productSnapshots.flatMap((snapshot) =>
          snapshot.docs.map((doc) => ({
            ...(doc.data() as Product),
            documentId: doc.id,
          }))
        );
      }

      // Merge wishlist with product data
      const products: Product[] = wishlists
        .map((wishlist) => {
          const product = wishlistProducts.find(
            (wishlistProduct) =>
              wishlistProduct.createdTime === wishlist.productId
          );
          return product ? { ...product } : null;
        })
        .filter((product): product is Product => product !== null);

      wishlistPageData.products = products;
      wishlistPageData.wishlists = wishlists;
      wishlistPageData.totalRecords = totalRecords;

      dispatch(fetchWishlistSuccess(wishlistPageData));
    } catch (error) {
      console.error("Error fetching wishlist:", error);
      dispatch(fetchWishlistFailure("Error fetching wishlist items."));
    }
  };

export const createWishlist =
  (userId: number, productId: number) => async (dispatch: Dispatch) => {
    dispatch(fetchWishlistRequest());
    try {
      // Check if wishlist item already exists
      const existingWishlistProduct = await getUniqueWishlistByIDs(
        userId,
        productId
      );
      if (existingWishlistProduct && existingWishlistProduct.length) {
        throw new Error("Wishlist product already exists");
      }

      // Get the user's actual document ID
      const userDocumentId = await getUserDocumentId(userId);
      if (!userDocumentId) {
        throw new Error("User not found");
      }

      // Ensure the user document exists first
      const userDocRef = doc(db, "users", userDocumentId);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        await setDoc(
          userDocRef,
          {
            userId: userId,
            createdTime: Date.now(),
          },
          { merge: true }
        );
      }

      // Create wishlist item in users/{userDocumentId}/wishlist subcollection
      const wishlistRef = collection(db, "users", userDocumentId, "wishlist");
      const docRef = await addDoc(wishlistRef, {
        productId: productId,
        createdTime: Date.now(),
      });

      const docSnap = await getDoc(docRef);
      const wishlists: Wishlist[] = [
        {
          documentId: docSnap.id,
          userId: userId,
          productId: productId,
        },
      ];

      dispatch(fetchWishlistSuccess({ products: [], wishlists }));
    } catch (error) {
      console.error("Error creating wishlist:", error);
      dispatch(fetchWishlistFailure("Error creating wishlist item."));
    }
  };

export const removeWishlist =
  (userId: number, productId: number) => async (dispatch: any) => {
    dispatch(removeWishlistRequest());
    try {
      // Get the user's actual document ID
      const userDocumentId = await getUserDocumentId(userId);
      if (!userDocumentId) {
        throw new Error("User not found");
      }

      // Remove from users/{userDocumentId}/wishlist subcollection
      const wishlistRef = collection(db, "users", userDocumentId, "wishlist");
      const q = query(wishlistRef, where("productId", "==", productId));
      const querySnapshot = await getDocs(q);

      const deletePromises = querySnapshot.docs.map((doc) =>
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);

      dispatch(removeWishlistSuccess());
      dispatch(fetchWishlistProductByUniqueId(userId, productId));
    } catch (error) {
      console.error("Error removing wishlist:", error);
      dispatch(removeWishlistFailure("Error removing wishlist item."));
    }
  };

export const fetchWishlistProductByUniqueId =
  (userId: number, productId: number) => async (dispatch: Dispatch) => {
    dispatch(fetchWishlistRequest());
    try {
      const wishlistPageData: {
        products: Product[];
        wishlists: Wishlist[];
        totalRecords?: number;
      } = {
        products: [],
        wishlists: [],
        totalRecords: 0,
      };

      const wishlistProduct = await getUniqueWishlistByIDs(userId, productId);
      wishlistPageData.products = [];
      wishlistPageData.wishlists = wishlistProduct;

      dispatch(fetchWishlistSuccess(wishlistPageData));
    } catch (error) {
      console.error("Error fetching wishlist item:", error);
      dispatch(fetchWishlistFailure("Error fetching wishlist item."));
    }
  };

const getUniqueWishlistByIDs = async (userId: number, productId: number) => {
  try {
    const wishlists: Wishlist[] = [];

    // Get the user's actual document ID
    const userDocumentId = await getUserDocumentId(userId);
    if (!userDocumentId) {
      return wishlists; // Return empty array if user not found
    }

    // Query users/{userDocumentId}/wishlist subcollection
    const wishlistRef = collection(db, "users", userDocumentId, "wishlist");
    const q = query(wishlistRef, where("productId", "==", productId));
    const querySnapshot = await getDocs(q);

    querySnapshot.forEach((doc) => {
      wishlists.push({
        documentId: doc.id,
        userId: userId,
        productId: doc.data().productId,
      });
    });

    return wishlists;
  } catch (error) {
    console.error("Error checking wishlist:", error);
    throw new Error("Error fetching wishlist item.");
  }
};
